/** @format */

// src/components/Footer.tsx
import { motion, useScroll, useAnimation } from "framer-motion";
import { Link } from "react-router-dom";
import { useEffect } from "react";

interface BaseLink {
  nome: string;
  url: string;
}

interface LinkWithIcon extends BaseLink {
  icon?: string | React.ReactElement;
}

export default function Footer() {
  const { scrollY } = useScroll();
  const controls = useAnimation();

  useEffect(() => {
    const footerElement = document.querySelector("footer");

    const unsubscribe = scrollY.on("change", (latest: number) => {
      if (footerElement) {
        const footerTop = footerElement.offsetTop;
        const windowHeight = window.innerHeight;
        const scrollPosition = latest + windowHeight;

        if (scrollPosition >= footerTop) {
          controls.start({ opacity: 1, y: 0 });
        } else {
          controls.start({ opacity: 0, y: 20 });
        }
      }
    });

    return () => unsubscribe();
  }, [controls, scrollY]);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const footerLinks: Record<string, LinkWithIcon[]> = {
    empresa: [
      { nome: "Sobre Nós", url: "/quem-somos" },
      { nome: "Nossa História", url: "/quem-somos" },
      { nome: "Equipa", url: "/quem-somos" },
    ],
    servicos: [
      { nome: "Hardware", url: "/servicos/hardware" },
      { nome: "Software", url: "/servicos/software" },
      { nome: "Renting", url: "/servicos/renting" },
      { nome: "Academia", url: "/academia" },
    ],
    contato: [
      { nome: "+244 947 137 676", url: "tel:+244947137676", icon: "📞" },
      {
        nome: "geral@envisio.co.ao",
        url: "mailto:geral@envisio.co.ao",
        icon: "📧",
      },
    ],
    social: [
      {
        nome: "LinkedIn",
        url: "#",
        icon: (
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
            <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
          </svg>
        ),
      },
      {
        nome: "Instagram",
        url: "#",
        icon: (
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
            <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
          </svg>
        ),
      },
      {
        nome: "Facebook",
        url: "#",
        icon: (
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
            <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
          </svg>
        ),
      },
    ],
  };

  return (
    <footer className="bg-black relative z-10 shadow-2xl">
      {/* Botão Voltar ao Topo */}
      <motion.button
        initial={{ opacity: 0, y: 20 }}
        animate={controls}
        onClick={scrollToTop}
        className="fixed right-8 bottom-8 bg-red-600 hover:bg-red-700 text-white w-12 h-12 rounded-full flex items-center justify-center shadow-lg transition-colors duration-300 z-50">
        <svg
          className="w-6 h-6"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M5 10l7-7m0 0l7 7m-7-7v18"
          />
        </svg>
      </motion.button>

      <div className="container mx-auto px-6 pt-10 pb-12 relative z-10">
        <div className="grid lg:grid-cols-5 gap-8">
          {/* Logo e Endereço */}
          <div className="lg:col-span-2 space-y-4">
            <Link to="/">
              <img
                src="/images/Logos/logo footer.svg"
                alt="Envisio"
                className="h-14"
              />
            </Link>
            <p className="text-white text-sm max-w-sm">
              Condomínio Jardins do Talatona, Torre 5 - Nº 003 Distrito Urbano
              do Talatona, Luanda - Angola
            </p>
          </div>

          {/* Links Rápidos */}
          {Object.entries(footerLinks)
            .slice(0, 3)
            .map(([categoria, links]) => (
              <div key={categoria} className="space-y-4">
                <h3 className="text-sm font-semibold text-white uppercase">
                  {categoria}
                </h3>
                <ul className="space-y-2">
                  {links.map((link) => (
                    <li key={link.nome}>
                      <a
                        href={link.url}
                        className="text-white hover:text-white text-sm flex items-center gap-2">
                        {link.icon && <span>{link.icon}</span>}
                        {link.nome}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
        </div>

        {/* Linha Separadora e Copyright */}
        <div className="mt-12 pt-8 border-t border-gray-200">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <p className="text-white text-sm">
              © {new Date().getFullYear()} Envisio. Todos os direitos
              reservados.
            </p>
            <div className="flex gap-4">
              {footerLinks.social.map((rede) => (
                <a
                  key={rede.nome}
                  href={rede.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white hover:text-red-500 transition-colors duration-300">
                  {typeof rede.icon === "string" ? (
                    <span className="text-xl">{rede.icon}</span>
                  ) : (
                    rede.icon
                  )}
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
